import { Component, Input, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { IUnit } from "@app/interfaces/IPeripheral";
import { IPlot } from "@app/interfaces/IPlot";
import { IVehicle } from "@app/interfaces/IVehicle";
import { UnitsService } from "@app/main/modules/foundation/_services/unit.service";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { DateService } from "@app/services/date.service";
import { MapService } from "@app/services/map.service";
import { ReasonCodeService } from "@app/services/reasonCode.service";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, of, Subscription } from "rxjs";
import { finalize, switchMap, tap } from "rxjs/operators";
import './unit-plots.component.scss';

@UntilDestroy()
@Component({
    selector: 'unit-plots',
    template: require('./unit-plots.component.html')
})
export class UnitPlotsComponent implements OnInit {
    @Input() unit: IUnit;

    plots: IPlot[];

    tableColumns: IVTableColumn<IPlot>[];
    tableRows: IVTableRow[];

    startDate: Date;
    endDate: Date;

    lastPlotDate: Date;
    selectedPlot: IPlot;
    selectedPlotVehicle: IVehicle;
    locationMarker: IGoogleMapsMarker;

    loading: boolean;

    plotsSubscription: Subscription;
    plotVehicleSubscription: Subscription;

    constructor(
        private readonly dateService: DateService,
        private readonly mapService: MapService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly unitConversionService: UnitConversionService,
        private readonly unitService: UnitsService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.loadData();
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IPlot>): void {
        if (filterEvent.trigger == "date" && filterEvent.sourcedata != undefined) {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.onDateChanged();
        }
    }

    onDateChanged(): void {
        this.loading = true;
        this.plotsSubscription?.unsubscribe();
        this.plotsSubscription = this.unitService.getUnitPlotsByImei(this.unit.Imei, this.startDate, this.endDate).pipe(untilDestroyed(this), finalize(() => this.loading = false)).subscribe(plots => {
            this.plots = plots;
            this.tableRows = this.vTableService.createTableRows(plots, this.tableColumns);
        })
    }

    onRowClicked(row: IVTableRow): void {
        this.selectedPlot = row.data;
        this.locationMarker = this.mapService.createMapMarker(row.data, { label: row.data.Velocity.toString(), showLabel: true });
        this.getPlotVehicle();
    }

    closeSidebar(): void {
        this.selectedPlot = null;
        this.locationMarker = null;
        this.plotVehicleSubscription?.unsubscribe();
    }

    getLastAvailablePlots(): void {
        this.startDate = this.dateService.getStartOfDay(this.lastPlotDate);
        this.endDate = this.dateService.getEndOfDay(this.lastPlotDate);
        this.onDateChanged();
    }

    //--

    private loadData(): void {
        this.createTables();
        this.initialiseStartAndEndDates();
        this.loading = true;
        this.plotsSubscription = this.getPlots().pipe(untilDestroyed(this), finalize(() => this.loading = false)).subscribe();
    }

    private getPlots(): Observable<any> {
        return this.unitService.getUnitPlotsByImei(this.unit.Imei, this.startDate, this.endDate).pipe(
            switchMap(plots => {
                if (plots.length) {
                    this.plots = plots;
                    this.tableRows = this.vTableService.createTableRows(plots, this.tableColumns);
                    return of(true);
                }
                return this.unitService.getLatestUnitPlotsByImei(this.unit.Imei, 1).pipe(
                    tap(plot => {
                        this.lastPlotDate = plot.length && new Date(plot[0].Utc);
                    })
                )
            })
        )
    }

    private initialiseStartAndEndDates(): void {
        this.startDate = this.dateService.getStartOfDay(new Date());
        this.endDate = this.dateService.getEndOfDay(new Date());
    }

    private getPlotVehicle(): void {
        this.plotVehicleSubscription = this.vehicleService.getVehicle(this.selectedPlot.VehicleId).pipe(untilDestroyed(this)).subscribe(vehicle => {
            this.selectedPlotVehicle = vehicle;
        })
    }

    private createTables(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Time", width: 180, sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: plot => this.vTableService.createDateCell(plot.Utc)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Satellites", width: 120, sortType: SortType.Number }),
                createCellFunction: plot => this.vTableService.createTableCell(plot.Sats)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Rssi", width: 60, sortType: SortType.Number }),
                createCellFunction: plot => this.vTableService.createTableCell(plot.Rssi)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reason", width: 120 }),
                createCellFunction: plot => this.vTableService.createTableCell(this.reasonCodeService.getReasonCodeLabel(plot.Reason))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Velocity", width: 100, sortType: SortType.Number }),
                createCellFunction: plot => this.vTableService.createTableCell(plot.Velocity + " kph", { sortData: plot.Velocity })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Odometer", width: 140, sortType: SortType.Number }),
                createCellFunction: plot => this.vTableService.createTableCell(this.unitConversionService.smartConvert(plot.Odometer / 1000, UnitConversionType.Distance), { sortData: plot.Odometer })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Location", sortable: false }),
                createCellFunction: plot => this.vTableService.createLocationInformationCell({ plot })
            }
        ]
    }
}
